import { useCallback, useEffect, useMemo, useState } from 'react';
import { AussparungBaum } from './AussparungBaum';
import { AussparungArbeit } from './AussparungArbeit';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, AussparungsUebersicht, Aussparung } from '../api';
import { Fachaktionen } from './Fachaktionen';
import { IconHerunterladen } from '../Icons';
import { AussparungAbgleich } from './AussparungAbgleich';
import { GewerkUnterlagenZip } from '../UnterlagenZipKnopf';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, Feld, KpiCard, KpiReihe,
  LoadingState, StatusBadge, type Spalte,
} from '../muster';
import { artname, bauartname, raumbefundname } from '../aussparungKlartext';
import { KatasterExport } from './aussparungen/KatasterExport';

/**
 * Die Aussparungsverwaltung und die Brandschottliste.
 *
 * --- Warum die Sperren ganz oben stehen ---------------------------------------
 *
 * Bei 4.164 von 4.449 gefüllten Feldern steht als Brandschutzanforderung
 * „TBC?" — to be confirmed. Eine Brandschottliste ohne belegte Anforderung ist
 * kein Nachweis, sondern eine Liste, die wie einer aussieht.
 *
 * Und bei 3.534 von 5.665 ist das Gewerk kombiniert („RLTAHZGA"). Ob die Nummer
 * ein Sammelkürzel trägt oder das führende Gewerk, ist eine Festlegung des
 * Bauherrn — eine vergebene Nummer steht im Modell und auf der Baustelle und
 * ist nicht zurückzuholen.
 *
 * --- Was die Seite noch nicht kann --------------------------------------------
 *
 * Sie vergibt keine Nummern und schreibt keinen Freigabestand zurück. Beides
 * hängt an Festlegungen, die der Bauherr treffen muss.
 */

interface Rahmen { api: ApiClient; projekt?: { id: string } }

// Die Namen stehen in ../aussparungKlartext. Die Liste hier kannte bis
// AS1 VIER Bauarten von sieben - 'tt_platte', 'unterzug' und 'stuetze'
// fehlten, und genau die drei stehen an 10.627 der 19.368 Hostzeilen.

// Die Namen stehen in ../aussparungKlartext. Die Liste hier kannte bis
// AS1 VIER Bauarten von sieben - 'tt_platte', 'unterzug' und 'stuetze'
// fehlten, und genau die drei stehen an 10.627 der 19.368 Hostzeilen.

export function Aussparungen() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [u, setU] = useState<AussparungsUebersicht | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [abschnitt, setAbschnitt] = useState('bestand');

  const laden = useCallback(() => {
    if (!projekt) return;
    api.aussparungenUebersicht(projekt.id)
      .then((x) => { setU(x); setFehler(null); })
      .catch(setFehler);
  }, [api, projekt]);

  useEffect(() => { laden(); }, [laden]);

  const kopf = (
    <PageHeader
      titel="Aussparungen"
      beschreibung={'Aussparungsbestand und Brandschottliste. Der Lauf rechnet: '
        + 'Welches Bauteil durchbrochen wird, steht bei 90 % der Aussparungen nicht '
        + 'im Modell und entsteht aus der Schnittmenge des Aussparungskörpers mit '
        + 'den Wänden der verknüpften Modelle.'}
      meta={u && <>{u.gesamt.toLocaleString('de-DE')} Aussparungen · {u.jeGeschoss.length} Geschosse</>}
    >
      {u && u.gesamt > 0 && (
        <Abschnitte gewaehlt={abschnitt} waehlen={setAbschnitt} />
      )}
    </PageHeader>
  );

  if (!projekt) {
    return (
      <>
        {kopf}
        <LoadingState text="Projekt wird geladen …" zeilen={5} />
      </>
    );
  }

  const leer = !u || u.gesamt === 0;

  return (
    <>
      {kopf}

      {/* Die Auftragsart 100. */}
      <Fachaktionen
        api={api}
        rolleKey="aussparungen"
        leseArt={100}
        leseText="Modell lesen"
      >
        {/* Koordination: Anforderungsliste, Brandschottkataster,
            Fremdmodellbericht und die Dashboards je Modell in einem Paket. */}
        <GewerkUnterlagenZip api={api} projektId={projekt.id} gewerk="koordination" />
      </Fachaktionen>

      {!leer && <Schottkette api={api} projektId={projekt.id} />}

      {!leer && <Schottnachweis api={api} projektId={projekt.id} />}

      {fehler !== null && (
        <ErrorState
          meldung="Die Aussparungsübersicht konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      )}

      {u && u.sperren.length > 0 && <Sperren sperren={u.sperren} />}

      {leer ? (
        <EmptyState
          titel="Noch kein Aussparungsbestand."
          erklaerung={'Er entsteht aus einem Auftrag mit der Aussparungsaufnahme — oben '
            + '„Modell lesen". Erwartet werden rund 5.665 Aussparungen. Der Auftrag muss '
            + 'auf dem conVoid-Aussparungsmodell laufen, und das Architektur- sowie das '
            + 'Tragwerksmodell müssen verknüpft UND geladen sein — ohne sie gibt es keine '
            + 'Bauart und damit keine Brandschottwahl.'}
        />
      ) : (
        <>
          <Kennzahlen u={u!} />

          {abschnitt === 'bestand' && <Bestand u={u!} />}
          {abschnitt === 'arbeit' && (
            <AussparungArbeit api={api} projektId={projekt.id} />
          )}
          {abschnitt === 'baum' && (
            <AussparungBaum api={api} projektId={projekt.id} />
          )}
          {abschnitt === 'liste' && (
            <Liste api={api} projektId={projekt.id} />
          )}
          {abschnitt === 'abgleich' && (
            <AussparungAbgleich api={api} projektId={projekt.id} />
          )}
        </>
      )}
    </>
  );
}

/**
 * Die Zahlen über den Reitern.
 *
 * Sie stehen ausserhalb der Reiter, weil sie fuer jeden gelten: Wer im Baum
 * arbeitet, will dieselben vier Zahlen sehen wie der, der die Liste
 * durchgeht.
 *
 * Die Auswahl folgt der Kette, an der die Zuordnung haengt: Ohne
 * durchbrochenes Bauteil keine Bauart, ohne Bauart kein Schott. Und ohne
 * gepruefte Anforderung ist das Schott nicht bemessbar - das ist der
 * BLOCKER, den die Schottzuordnung selbst meldet.
 */
function Kennzahlen({ u }: { u: AussparungsUebersicht }) {
  const anteil = (n: number) =>
    (u.gesamt > 0 ? `${Math.round(100 * n / u.gesamt)} % von ${u.gesamt.toLocaleString('de-DE')}` : undefined);

  return (
    <KpiReihe>
      <KpiCard titel="Aussparungen" wert={u.gesamt}
               vergleich={`${u.jeGeschoss.length} Geschosse`} />
      <KpiCard
        titel="Mit Bauteil" wert={u.mitHost} vergleich={anteil(u.mitHost)}
        zustand={u.mitHost < u.gesamt ? 'warnung' : undefined}
        zustandText={u.mitHost < u.gesamt ? 'ohne Bauteil keine Bauart' : undefined}
      />
      <KpiCard titel="Mit Durchgang" wert={u.mitDurchgang}
               vergleich={anteil(u.mitDurchgang)} />
      <KpiCard titel="Brandschotts" wert={u.brandschotts} />
      <KpiCard
        titel="Ohne Anforderung" wert={u.ohneAnforderung}
        vergleich={anteil(u.ohneAnforderung)}
        zustand={u.ohneAnforderung > 0 ? 'warnung' : undefined}
        zustandText={u.ohneAnforderung > 0 ? 'Schott nicht bemessbar' : undefined}
      />
      {/*
        Die stillgelegten stehen NEBEN dem Bestand und nicht darin: Sie sind
        kein Durchbruch mehr, und ihre Nummern sind trotzdem vergeben. Wer
        die Karte sieht, weiß, wonach er im Filter „entfernt" suchen kann.
      */}
      <KpiCard
        titel="Entfernt (stillgelegt)" wert={u.entfernt}
        vergleich={u.nummernReserviert > 0
          ? `${u.nummernReserviert.toLocaleString('de-DE')} Nummern reserviert`
          : undefined}
      />
    </KpiReihe>
  );
}

function Sperren({ sperren }: { sperren: AussparungsUebersicht['sperren'] }) {
  const blocker = sperren.filter((s) => s.gewicht === 'blocker');

  return (
    <ContentSection
      titel={blocker.length > 0
        ? 'Die Brandschottliste kann noch nicht geführt werden'
        : 'Offene Punkte'}
    >
      {sperren.map((s) => (
        <div key={s.kennung} style={{ marginBottom: 14 }}>
          <StatusBadge
            zustand={s.gewicht === 'blocker' ? 'fehler' : 'warnung'}
            text={s.titel}
          />
          <p style={{ margin: '4px 0 0', fontSize: 13 }}>{s.text}</p>
        </div>
      ))}
    </ContentSection>
  );
}

function Abschnitte({ gewaehlt, waehlen }: {
  gewaehlt: string; waehlen: (x: string) => void;
}) {
  return (
    <div className="reiter" role="tablist" style={{ marginBottom: 14 }}>
      {([['bestand', 'Bestand'], ['arbeit', 'Arbeitsansicht'],
         ['baum', 'Baum nach Bauart'],
         ['liste', 'Aussparungsliste'],
         ['abgleich', 'Abgleich Architektur']] as const)
        .map(([k, name]) => (
          <button key={k}
                  type="button"
                  role="tab"
                  aria-selected={gewaehlt === k}
                  className={`reiter-knopf${gewaehlt === k ? ' aktiv' : ''}`}
                  onClick={() => waehlen(k)}>
            {name}
          </button>
        ))}
    </div>
  );
}

function Bestand({ u }: { u: AussparungsUebersicht }) {
  return (
    <>
      <KpiReihe>
        <KpiCard titel="Aussparungen" wert={u.gesamt} />
        <KpiCard titel="Mit durchbrochenem Bauteil" wert={u.mitHost} />
        <KpiCard titel="Mit Raum" wert={u.mitRaum} />
        {/* 'zählend' steht dabei, weil die Zahl es sagt: Dämmungen und
            Anschlusskanäle an Brandschutzklappen gehen nicht ein. Ohne das
            Wort läse man 1.523 und verglich es mit 3.738 Durchgängen. */}
        <KpiCard
          titel="Mit zählendem Durchgang" wert={u.mitDurchgang}
          fussnote={'Dämmungen neben ihrem Rohr und Anschlusskanäle an einer '
            + 'Brandschutzklappe zählen nicht mit.'}
        />
        <KpiCard
          titel="Ohne Anforderung" wert={u.ohneAnforderung}
          zustand={u.ohneAnforderung > 0 ? 'fehler' : undefined}
          zustandText={u.ohneAnforderung > 0 ? 'Schott nicht bemessbar' : undefined}
        />
        <KpiCard titel="Brandschottnummern" wert={u.brandschotts} />
      </KpiReihe>

      <DataTable
        name="Je Geschoss"
        spalten={[
          {
            schluessel: 'geschoss', titel: 'Geschoss', immerSichtbar: true,
            wert: (g: AussparungsUebersicht['jeGeschoss'][number]) => g.geschoss ?? '',
            inhalt: (g) => g.geschoss ?? <em className="schwach">ohne</em>,
          },
          { schluessel: 'stueck', titel: 'Stück', art: 'zahl', wert: (g) => g.stueck },
          { schluessel: 'rund', titel: 'rund', art: 'zahl', wert: (g) => g.rund },
          { schluessel: 'eckig', titel: 'eckig', art: 'zahl', wert: (g) => g.eckig },
          {
            schluessel: 'horizontal', titel: 'horizontal', art: 'zahl',
            wert: (g) => g.horizontal,
          },
          {
            schluessel: 'vertikal', titel: 'vertikal', art: 'zahl',
            wert: (g) => g.vertikal,
          },
          {
            schluessel: 'anforderung', titel: 'mit Anforderung', art: 'zahl',
            wert: (g) => g.mitAnforderung,
            inhalt: (g) => (g.mitAnforderung === 0
              ? <StatusBadge zustand="fehler" text="0" klein
                             titel="Kein Schott in diesem Geschoss bemessbar" />
              : g.mitAnforderung.toLocaleString('de-DE')),
          },
          {
            schluessel: 'dichtung', titel: 'Dichtung', art: 'zahl', einheit: 'm³',
            nachkomma: 2, wert: (g) => g.dichtungM3,
          },
        ]}
        zeilen={u.jeGeschoss}
        schluessel={(g) => g.geschoss ?? 'ohne'}
        ausfuhr
      />

      <DataTable
        name="Zustände"
        beschreibung={'conVoid führt ZWEI Sprachen im selben Feld: „Created" und '
          + '„Erstellt" sind derselbe Zustand, „Up to Date" und „Aktuell" auch. Eine '
          + 'Abfrage auf einen der beiden verlöre den anderen — deshalb steht die '
          + 'Abbildung in einer pflegbaren Tabelle.'}
        spalten={[
          {
            schluessel: 'roh', titel: 'im Modell', immerSichtbar: true,
            wert: (s2: AussparungsUebersicht['jeZustand'][number]) => s2.zustandRoh ?? '',
            inhalt: (s2) => <span className="schwach">{s2.zustandRoh ?? '—'}</span>,
          },
          {
            schluessel: 'zustand', titel: 'Zustand', art: 'status',
            wert: (s2) => s2.zustand ?? '',
            inhalt: (s2) => (s2.zustand
              ? <span>{s2.zustand}</span>
              : <StatusBadge zustand="warnung" text="nicht abgebildet" klein />),
          },
          { schluessel: 'stueck', titel: 'Stück', art: 'zahl', wert: (s2) => s2.stueck },
        ]}
        zeilen={u.jeZustand}
        schluessel={(s2) => s2.zustandRoh ?? 'ohne'}
        suche={false}
      />

      <DataTable
        name="Gewerke"
        beschreibung={'Das FÜHRENDE Gewerk bleibt leer, solange es kombiniert ist. Ob '
          + 'die Brandschottnummer ein Sammelkürzel trägt oder das führende Gewerk, ist '
          + 'eine Festlegung — nicht das erste im String.'}
        spalten={[
          {
            schluessel: 'roh', titel: 'im Modell', immerSichtbar: true,
            wert: (g: AussparungsUebersicht['jeGewerk'][number]) => g.gewerkRoh ?? '',
            inhalt: (g) => <span className="schwach">{g.gewerkRoh ?? '—'}</span>,
          },
          {
            schluessel: 'gewerke', titel: 'aufgelöst', art: 'status',
            wert: (g) => g.gewerke ?? '',
            inhalt: (g) => (g.gewerke?.includes(',')
              ? <StatusBadge zustand="warnung" text={g.gewerke} klein
                             titel="kombiniert — Nummernschema offen" />
              : g.gewerke ?? '—'),
          },
          { schluessel: 'stueck', titel: 'Stück', art: 'zahl', wert: (g) => g.stueck },
        ]}
        zeilen={u.jeGewerk}
        schluessel={(g) => g.gewerkRoh ?? 'ohne'}
        ausfuhr
      />

      {u.jeBauart.length > 0 && (
        <DataTable
          name="Bauart der durchbrochenen Bauteile"
          beschreibung={'Aus der TRAGENDEN SCHICHT und nicht aus dem ersten Eintrag im '
            + 'Materialstring: „DÄMMUNG-XPS/STB-Ortbeton" ist eine Stahlbetonwand mit '
            + 'Dämmung. Ein Schott in einer Trockenbauwand mit Betonvorsatz ist ein '
            + 'anderes Produkt als in Stahlbeton.'}
          spalten={[
            {
              schluessel: 'bauart', titel: 'Bauart', immerSichtbar: true,
              wert: (b: AussparungsUebersicht['jeBauart'][number]) => b.bauart ?? '',
              inhalt: (b) => (b.bauart
                ? bauartname(b.bauart)
                : <em className="schwach">unbestimmt</em>),
            },
            { schluessel: 'stueck', titel: 'Bauteile', art: 'zahl', wert: (b) => b.stueck },
          ]}
          zeilen={u.jeBauart}
          schluessel={(b) => b.bauart ?? 'ohne'}
          suche={false}
        />
      )}
    </>
  );
}

function Liste({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [daten, setDaten] = useState<{
    gesamt: number; seite: number; proSeite: number; zeilen: Aussparung[];
  } | null>(null);

  const [fehler, setFehler] = useState<unknown>(null);
  const [seite, setSeite] = useState(1);
  const [geschoss, setGeschoss] = useState('');
  const [bereich, setBereich] = useState('');
  const [nurOffene, setNurOffene] = useState(false);

  // ═══════════════════════════════════════════════════════════════════════
  // DER EINE ORT, AN DEM DIE STILLGELEGTEN ZU SEHEN SIND
  // ═══════════════════════════════════════════════════════════════════════
  //
  // F87 nimmt sie überall heraus — aus dem Baum, dem Arbeitsvorrat, der
  // Ampel, dem Kataster und der Rückfrage an die Architektur. Hier stehen
  // sie: 395 Aussparungen mit 296 reservierten Nummern, jede mit Datum und
  // Grund. Ohne diesen Schalter wären sie nicht korrigiert, sondern
  // verschwunden — und niemand könnte prüfen, ob das Modell recht hat.
  const [nurEntfernte, setNurEntfernte] = useState(false);

  const [bereiche, setBereiche] = useState<{
    planungsbereich: string | null; ohneAnforderung: number;
    stueck: number;
  }[]>([]);

  // Die Bereiche einmal - sie aendern sich nur mit einem Modelllauf.
  useEffect(() => {
    api.aussparungBereiche(projektId)
      .then(setBereiche)
      .catch(() => setBereiche([]));
  }, [api, projektId]);

  useEffect(() => {
    setFehler(null);
    api.aussparungen(projektId, {
      seite, geschoss: geschoss || undefined,
      planungsbereich: bereich || undefined,
      ohneAnforderung: nurOffene || undefined,
      entfernt: nurEntfernte || undefined,
    })
      .then(setDaten)
      .catch((e) => { setDaten(null); setFehler(e); });
  }, [api, projektId, seite, geschoss, bereich, nurOffene, nurEntfernte]);

  const spalten: Spalte<Aussparung>[] = useMemo(() => [
    {
      schluessel: 'kennzeichen', titel: 'Kennzeichen', immerSichtbar: true,
      breite: '160px',
      wert: (a2) => a2.kennzeichen ?? a2.revitElementId,
    },
    {
      schluessel: 'geschoss', titel: 'Geschoss', breite: '110px',
      wert: (a2) => a2.geschoss ?? '',
      inhalt: (a2) => a2.geschoss ?? '—',
    },
    {
      schluessel: 'form', titel: 'Form', breite: '100px',
      wert: (a2) => a2.form ?? '',
      inhalt: (a2) => a2.form ?? '—',
    },
    {
      schluessel: 'mass', titel: 'Maß', einheit: 'mm', ausrichtung: 'rechts',
      breite: '140px',
      // Sortiert wird nach der grössten Kante — ein Durchmesser ist mit einer
      // Breite sonst nicht vergleichbar.
      wert: (a2) => a2.durchmesserMm ?? Math.max(a2.breiteMm ?? 0, a2.hoeheMm ?? 0),
      inhalt: (a2) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {a2.durchmesserMm
            ? `⌀ ${a2.durchmesserMm}`
            : `${a2.breiteMm ?? '?'} × ${a2.hoeheMm ?? '?'}`}
        </span>
      ),
    },
    {
      schluessel: 'gewerk', titel: 'Gewerk', art: 'status', breite: '150px',
      wert: (a2) => a2.gewerke ?? '',
      inhalt: (a2) => {
        if (!a2.gewerke) return <em className="schwach">—</em>;
        return a2.gewerke.includes(',')
          ? <StatusBadge zustand="warnung" text={a2.gewerke} klein
                         titel="kombiniert — Nummernschema offen" />
          : <span>{a2.gewerke}</span>;
      },
    },
    {
      schluessel: 'zustand', titel: 'Zustand', breite: '130px',
      wert: (a2) => a2.zustand ?? a2.zustandRoh ?? '',
      inhalt: (a2) => (
        <span className="schwach">{a2.zustand ?? a2.zustandRoh ?? '—'}</span>
      ),
    },
    {
      schluessel: 'anforderung', titel: 'Anforderung', art: 'status', breite: '160px',
      wert: (a2) => a2.anforderungGeprueft ?? a2.anforderungRoh ?? '',
      inhalt: (a2) => (a2.anforderungGeprueft
        ? <StatusBadge zustand="gut" text={a2.anforderungGeprueft} klein />
        : (
          <StatusBadge
            zustand="warnung" klein
            text={a2.anforderungRoh ?? '—'}
            titel="Nicht geprüft — das Schott ist damit nicht bemessbar"
          />
        )),
    },
    {
      schluessel: 'hosts', titel: 'durchbricht', sortierbar: false,
      wert: (a2) => a2.hosts.map((h) => h.bauart ?? '').join(', '),
      inhalt: (a2) => (
        <span className="schwach" style={{ fontSize: 11 }}>
          {a2.hosts.length === 0
            ? <em>keins</em>
            : a2.hosts.map((h, i) => (
              <div key={i}>
                {bauartname(h.bauart)}
                {h.dickeMm && ` ${h.dickeMm} mm`}
              </div>
            ))}
        </span>
      ),
    },
    {
      schluessel: 'raeume', titel: 'Räume', sortierbar: false,
      wert: (a2) => a2.raeume.map((r) => r.raumnummer ?? '').join(', '),
      inhalt: (a2) => (
        <span className="schwach" style={{ fontSize: 11 }}>
          {a2.raeume.map((r) => (
            <div key={r.seite}>
              {r.seite}: {r.raumnummer ?? raumbefundname(r.befund)}
            </div>
          ))}
        </span>
      ),
    },
    {
      schluessel: 'durchgang', titel: 'Durchgang', sortierbar: false,
      wert: (a2) => a2.durchgaenge.length,
      inhalt: (a2) => (
        <span className="schwach" style={{ fontSize: 11 }}>
          {a2.durchgaenge.length === 0
            ? <em>keiner</em>
            : a2.durchgaenge.slice(0, 3).map((dg, i) => (
              <div key={i}>
                {artname(dg.art)}
                {dg.nennweite && ` DN ${dg.nennweite}`}
                {dg.istBrennbar && (
                  <> <StatusBadge zustand="warnung" text="brennbar" klein
                                  titel="andere Durchführung" /></>
                )}
              </div>
            ))}
          {a2.durchgaenge.length > 3
            && `+ ${a2.durchgaenge.length - 3} weitere`}
        </span>
      ),
    },

    // ═══════════════════════════════════════════════════════════════════
    // NUR IN DER ANSICHT DER STILLGELEGTEN
    // ═══════════════════════════════════════════════════════════════════
    //
    // Dort sind Nummer, Datum und Grund die Hauptsache: Ohne sie ist die
    // Liste eine Aufzählung von Kennzeichen, und die Frage „darf diese
    // Nummer wieder vergeben werden?" bleibt offen. Sie darf nie.
    ...(nurEntfernte ? [
      {
        schluessel: 'nummern', titel: 'reservierte Nummern',
        sortierbar: false,
        wert: (a2: Aussparung) => a2.nummern.map((n) => n.nummer).join(', '),
        inhalt: (a2: Aussparung) => (a2.nummern.length === 0
          ? <em className="schwach">keine</em>
          : (
            <span className="schwach" style={{ fontSize: 11 }}>
              {a2.nummern.map((n) => <div key={n.nummer}>{n.nummer}</div>)}
            </span>
          )),
      },
      {
        schluessel: 'entferntAm', titel: 'stillgelegt', breite: '120px',
        wert: (a2: Aussparung) => a2.entferntAm ?? '',
        inhalt: (a2: Aussparung) => (a2.entferntAm
          ? <span>{new Date(a2.entferntAm).toLocaleDateString('de-DE')}</span>
          : <em className="schwach">—</em>),
      },
      {
        schluessel: 'entferntGrund', titel: 'Grund', sortierbar: false,
        wert: (a2: Aussparung) => a2.entferntGrund ?? '',
        inhalt: (a2: Aussparung) => (
          <span className="schwach" style={{ fontSize: 11 }}>
            {a2.entferntGrund ?? '—'}
          </span>
        ),
      },
    ] as Spalte<Aussparung>[] : []),
  ], [nurEntfernte]);

  const seiten = daten ? Math.max(1, Math.ceil(daten.gesamt / daten.proSeite)) : 1;

  return (
    <DataTable
      name="Aussparungsliste"
      spalten={spalten}
      zeilen={daten?.zeilen ?? []}
      schluessel={(a2) => a2.id}
      laedt={daten === null && fehler === null}
      fehler={fehler}
      // Geblättert wird auf dem SERVER: `proSeite={0}` heisst „die Tabelle
      // blättert nicht selbst", die Trefferzahl kommt aus der Antwort.
      proSeite={0}
      trefferText={daten
        ? `${daten.gesamt.toLocaleString('de-DE')} `
          + (nurEntfernte
              ? 'stillgelegte Aussparungen (Nummern reserviert, F87)'
              : 'Aussparungen')
          + ` · Seite ${daten.seite} von ${seiten}`
        : undefined}
      suche={false}
      ausfuhr
      ausfuhrDateiname="Aussparungen"
      dicht
      werkzeuge={(
        <>
          <Feld
            beschriftung="Geschoss"
            wert={geschoss}
            beiAenderung={(w) => { setGeschoss(w as string); setSeite(1); }}
            platzhalter="z. B. G02"
          />

          {/* Der Planungsbereich: Danach ist die Arbeit organisiert. */}
          {bereiche.some((b) => b.planungsbereich) && (
            <Feld
              art="auswahl"
              beschriftung="Planungsbereich"
              wert={bereich}
              beiAenderung={(w) => { setBereich(w as string); setSeite(1); }}
              optionen={[
                { wert: '', beschriftung: 'alle Planungsbereiche' },
                ...bereiche.filter((b) => b.planungsbereich).map((b) => ({
                  wert: b.planungsbereich!,
                  beschriftung: `${b.planungsbereich} (${b.stueck})`,
                })),
              ]}
            />
          )}

          <Feld
            art="haken"
            beschriftung="nur ohne geprüfte Anforderung"
            wert={nurOffene}
            beiAenderung={(w) => { setNurOffene(w as boolean); setSeite(1); }}
          />

          <Feld
            art="haken"
            beschriftung="entfernt (stillgelegt)"
            wert={nurEntfernte}
            beiAenderung={(w) => { setNurEntfernte(w as boolean); setSeite(1); }}
          />

          <button className="m-knopf" disabled={seite <= 1}
                  onClick={() => setSeite(seite - 1)}>
            ← zurück
          </button>
          <button className="m-knopf" disabled={seite >= seiten}
                  onClick={() => setSeite(seite + 1)}>
            weiter →
          </button>
        </>
      )}
      leer={(
        <EmptyState
          titel="Keine Aussparung in dieser Auswahl."
          erklaerung="Die Filter oben sind zu eng gesetzt."
          aktion={(geschoss || bereich || nurOffene || nurEntfernte) ? (
            <button
              className="m-knopf"
              onClick={() => {
                setGeschoss(''); setBereich(''); setNurOffene(false);
                setNurEntfernte(false); setSeite(1);
              }}
            >
              Filter zurücksetzen
            </button>
          ) : undefined}
        />
      )}
    />
  );
}


/**
 * Die Brandschottkette: ableiten, zuordnen, nummerieren — und das Kataster
 * (als PDF und, seit AK1, als Excel: `KatasterExport`).
 *
 * --- Warum EIN Knopf für drei Schritte -------------------------------------
 *
 * Sie bauen aufeinander auf, und zwar in dieser Reihenfolge:
 *
 *   1  Anforderung ableiten   aus der Klasse des durchbrochenen Bauteils
 *   2  Schott zuordnen        braucht Anforderung UND Bauart
 *   3  Nummer vergeben        braucht eine vollständige Zuordnung
 *
 * Wer nur zuordnet, bekommt dasselbe Ergebnis wie vorher — die Anforderung
 * fehlt ja noch. Genau das ist einmal passiert: Nach dem Modelllauf lagen
 * 5.664 durchbrochene Bauteile vor, und die Zuordnung fand 141 Treffer.
 *
 * --- Warum die Nummernvergabe im selben Knopf steckt ------------------------
 *
 * Weil sie keine eigene Entscheidung ist: Eine Nummer bekommt nur, was
 * vollständig bestimmt ist, und eine bestehende Nummer wird nie geändert.
 * Der Knopf kann deshalb beliebig oft gedrückt werden.
 */
function Schottkette({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<any | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  const ausfuehren = async () => {
    setLaeuft('kette');
    setFehler(null);
    try {
      setErgebnis(await api.aussparungSchottkette(projektId));
    } catch (e) {
      setFehler(e);
    } finally {
      setLaeuft(null);
    }
  };

  return (
    <ContentSection
      titel="Brandschottkette"
      beschreibung={'Anforderung ableiten, Schott zuordnen, Nummer vergeben — in dieser '
        + 'Reihenfolge. Wer nur zuordnet, bekommt dasselbe Ergebnis wie vorher, weil die '
        + 'Anforderung fehlt. Eine Nummer bekommt nur, was vollständig bestimmt ist, und '
        + 'eine bestehende Nummer wird nie geändert — der Knopf darf beliebig oft '
        + 'gedrückt werden.'}
      aktionen={(
        <>
          <button className="m-knopf m-knopf-haupt" disabled={laeuft !== null}
                  onClick={ausfuehren}>
            {laeuft === 'kette'
              ? 'rechnet …'
              : 'Brandschotts zuweisen und nummerieren'}
          </button>

          <KatasterExport
            api={api}
            projektId={projektId}
            gesperrt={laeuft === 'kette'
              ? 'Die Brandschottkette rechnet; das Kataster folgt danach.'
              : undefined}
            beimLaden={(l) => setLaeuft(l ? 'kataster' : null)}
          />
        </>
      )}
    >
      {/* Die Nummernform steht im INHALT und nicht in `meta`: Der Kopf einer
          `ContentSection` schrumpft seine linke Spalte, wenn rechts viel
          steht — bei 1440 px blieb von der Beschreibung eine 80 px breite
          Säule übrig (gemessen am Abzug vom 18.09.). */}
      <p className="sehr-schwach" style={{ fontSize: 12 }}>
        Nummernform BRS_Ebene_Abschnitt_Gewerk_0000
        {' '}(san · hzg · klt · rlt · elt · spr · msr)
      </p>

      {fehler !== null && (
        <ErrorState klein meldung="Die Brandschottkette ist fehlgeschlagen."
                    technisch={fehler} />
      )}

      {ergebnis && (
        <>
          <KpiReihe>
            <KpiCard titel="Anforderungen abgeleitet"
                     wert={ergebnis.anforderungen?.abgeleitet ?? 0} />
            <KpiCard
              titel="Schotts zugeordnet" wert={ergebnis.schotts?.zugeordnet ?? 0}
              vergleich={`von ${(ergebnis.schotts?.durchgaenge ?? 0)
                .toLocaleString('de-DE')} Durchgängen`}
            />
            <KpiCard titel="Nummern neu vergeben"
                     wert={ergebnis.nummern?.vergeben ?? 0} />
            <KpiCard
              titel="Noch ohne Nummer" wert={ergebnis.nummern?.uebersprungen ?? 0}
              zustand={(ergebnis.nummern?.uebersprungen ?? 0) > 0 ? 'warnung' : undefined}
              zustandText={(ergebnis.nummern?.uebersprungen ?? 0) > 0
                ? 'Gründe unten' : undefined}
            />
          </KpiReihe>

          {/* Was NICHT durchging, und warum. Eine Zahl ohne ihren Grund
              lässt den Leser raten. */}
          {ergebnis.nummern?.jeGrund
            && Object.keys(ergebnis.nummern.jeGrund).length > 0 && (
            <DataTable
              name="Warum keine Nummer"
              spalten={[
                {
                  schluessel: 'grund', titel: 'Grund', immerSichtbar: true,
                  wert: (g: { grund: string; anzahl: number }) => g.grund,
                },
                {
                  schluessel: 'anzahl', titel: 'Anzahl', art: 'zahl',
                  wert: (g: { grund: string; anzahl: number }) => g.anzahl,
                },
              ]}
              zeilen={Object.entries(ergebnis.nummern.jeGrund)
                .map(([grund, anzahl]) => ({ grund, anzahl: Number(anzahl) }))}
              schluessel={(g) => g.grund}
              sortierung={{ spalte: 'anzahl', richtung: 'ab' }}
              suche={false}
              spaltenwahl={false}
              dicht
            />
          )}

          {(ergebnis.anforderungen?.hinweise ?? []).map(
            (h: string, i: number) => (
              <div key={i} className="hinweis warnung" style={{ fontSize: 12 }}>
                <span className="hinweis-zeichen">!</span><div>{h}</div>
              </div>
            ))}
        </>
      )}
    </ContentSection>
  );
}

/**
 * Der Schottnachweis als PDF -- gesamt und je Planungsabschnitt (Paket AS3).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM NEBEN DEM KATASTERKNOPF UND NICHT STATT SEINER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Es sind zwei Blätter mit zwei Adressaten. Das Kataster geht auf die
 * Baustelle: eine Zeile je Einbau, in der Ordnung der Arbeit. Der Nachweis
 * geht an den Prüfsachverständigen: Verfahren mit Norm, Freigabekriterien in
 * Zahlen, Befunde mit Fundstelle, Annahmen mit Herkunft, Referenzabgleich.
 * Wer das eine durch das andere ersetzte, nähme einem der beiden sein Blatt.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM JE ABSCHNITT EIN EIGENER KNOPF
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Weil danach die Arbeit organisiert ist. Das Gesamtblatt führt alle aktiven
 * Abschottungen; wer Abschnitt 3 prüft, braucht Abschnitt 3 -- und nicht 89
 * Seiten, von denen ihn ein Sechstel angeht. Muster: AussenBerichtKnopf (AU6),
 * wo derselbe Befund („je Bereich fehlen die PDF-Exporte") am 23.09.2026 vom
 * Fachplaner gemeldet wurde.
 */
function Schottnachweis({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [bereiche, setBereiche] = useState<string[]>([]);

  useEffect(() => {
    api.aussparungBereiche(projektId)
      .then((b) => setBereiche(
        [...new Set(b.map((x) => x.planungsbereich)
          .filter((x): x is string => !!x))].sort()))
      // Ohne Bereichsliste bleibt das Gesamtblatt erreichbar. Eine
      // Fehlermeldung beträfe die Aufteilung und nicht den Bericht.
      .catch(() => setBereiche([]));
  }, [api, projektId]);

  const hole = async (bereich?: string) => {
    setLaeuft(bereich ?? 'gesamt');
    setFehler(null);
    try {
      await api.schottnachweisPdf(projektId, bereich);
    } catch (e) {
      setFehler(e);
    } finally {
      setLaeuft(null);
    }
  };

  return (
    <ContentSection
      titel="Schottnachweis"
      beschreibung={'Das Blatt für den Prüfsachverständigen: das Verfahren mit '
        + 'seinen Normen (DIN 4102-11, DIN EN 1366-3, DIN EN 13501-2, MLAR 4.3), '
        + 'der abgeleitete Freigabestatus mit seinen Kriterien in Zahlen, eine '
        + 'Zeile je Abschottung mit Anforderung und Herkunft, die Befunde mit '
        + 'Fundstelle und im vollen Wortlaut, die Annahmen mit Herkunft und der '
        + 'Abgleich gegen LV, Architekturmodell und Nummernzähler. Nicht dasselbe '
        + 'wie das Kataster — das ist die Liste für die Baustelle.'}
      aktionen={(
        <button className="m-knopf m-knopf-haupt" disabled={laeuft !== null}
                onClick={() => hole()}
                title="Alle aktiven Abschottungen über alle Planungsabschnitte">
          <IconHerunterladen size={16} />
          {laeuft === 'gesamt' ? 'wird erzeugt …' : 'Schottnachweis gesamt'}
        </button>
      )}
    >
      {bereiche.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap',
                      alignItems: 'center' }}>
          <span className="sehr-schwach" style={{ fontSize: 12 }}>
            je Planungsabschnitt:
          </span>
          {bereiche.map((b) => (
            <button key={b} className="m-knopf m-knopf-still"
                    disabled={laeuft !== null} onClick={() => hole(b)}>
              {laeuft === b ? 'erzeugt …' : b}
            </button>
          ))}
        </div>
      )}

      <p className="sehr-schwach" style={{ fontSize: 12 }}>
        Der Status wird ABGELEITET und nie auf „Freigegeben" gesetzt (F89) —
        eine Freigabe ist die Unterschrift eines Menschen.
      </p>

      {fehler !== null && (
        <ErrorState klein meldung="Der Schottnachweis konnte nicht erzeugt werden."
                    technisch={fehler} />
      )}
    </ContentSection>
  );
}
